import { isCorporateAdminUser, isCorporateStudentUser } from '@/composables/useCorporateUserRole.js';
import { CorporateOnly } from '@/middleware/EdverumClientMiddleware.js';
import GateKeeperMiddleware from '@/middleware/GateKeeperMiddleware.js';

/**
 * Corporate-only routes.
 *
 * Two-layer gating applies to every entry below:
 *   1. Client axis  - `CorporateOnly` (beforeEnter) redirects non-corporate
 *      clients to /restricted. Wired up below as part of the default mapper.
 *   2. Role axis    - `corporateAdminOnly` meta gates the route to
 *      admin-roled corporate users; corporate students get redirected to
 *      /restricted via `GateAccessDenied`. Opt routes in by spreading
 *      `corporateAdminOnly` into their `meta` block.
 *
 * Add new entries to `routes` below; the module automatically attaches the
 * `CorporateOnly` route guard so non-corporate clients are redirected to 403
 * if they try to navigate directly. Drop the matching page component under
 * `resources/js/pages/corporate/` to keep corporate-only code isolated.
 *
 * See `docs/domains/client-variants.md` for the broader two-axis pattern
 * (client x role) and when to use each.
 */

const corporateAdminOnly = {
    middleware: [GateKeeperMiddleware],
    ability: () => isCorporateAdminUser(),
};

const corporateStudentOnly = {
    middleware: [GateKeeperMiddleware],
    ability: () => isCorporateStudentUser(),
};

const routes = [
    {
        path: '/manage-users',
        name: 'corporate-manage-users',
        component: () => import('@/pages/corporate/ManageUsers.vue'),
        meta: {
            title: 'Manage Users',
            ...corporateAdminOnly,
        },
    },
    {
        path: '/manage-settings',
        name: 'corporate-settings',
        component: () => import('@/pages/corporate/Settings.vue'),
        meta: {
            title: 'Manage Settings',
            ...corporateAdminOnly,
        },
    },
    {
        // Consolidated entry point for Slide Decks + Videos, reached from the
        // "Manage All Content" card on the Manage Settings page. The list views
        // are hosted as per-tab CHILD routes (one mounted at a time via the
        // parent's <router-view>) rather than both mounted under a `?tab=` query
        // param. That mirrors how SlideDeckIndex / VideoIndex host these same
        // views and keeps EdverumTable's one-table-per-route assumptions intact:
        // its archive toggle and pagination derive from `$route.name`, so each
        // tab needs its own registered route (incl. an `-archive` variant).
        //
        // The standalone /slide-decks and /videos routes remain for deep links
        // and the create/edit/view flows. The bare `manage-content` route only
        // redirects to the first permitted child (handled in the page).
        path: '/manage-content',
        name: 'manage-content',
        component: () => import('@/pages/corporate/ManageAllContent.vue'),
        meta: {
            title: 'Manage All Content',
            ...corporateAdminOnly,
        },
        children: [
            {
                name: 'manage-content-slide-decks',
                path: 'slide-decks',
                component: () => import('@/components/slide-decks/SlideDecksView.vue'),
                meta: {
                    title: 'Slide Decks',
                    middleware: [GateKeeperMiddleware],
                    ability: { action: 'view all', subject: 'slide decks' },
                },
                props: true,
            },
            {
                name: 'manage-content-slide-decks-archive',
                path: 'slide-decks/archive',
                component: () => import('@/components/slide-decks/SlideDecksView.vue'),
                meta: {
                    title: 'Archived Slide Decks',
                    middleware: [GateKeeperMiddleware],
                    ability: ({ gateKeeper }) => gateKeeper.userHasPermission('archive a slide deck'),
                },
                props: true,
            },
            {
                name: 'manage-content-videos',
                path: 'videos',
                component: () => import('@/components/videos/VideosView.vue'),
                meta: {
                    title: 'Videos',
                    middleware: [GateKeeperMiddleware],
                    ability: { action: 'view all', subject: 'videos' },
                },
                props: true,
            },
            {
                name: 'manage-content-videos-archive',
                path: 'videos/archive',
                component: () => import('@/components/videos/VideosView.vue'),
                meta: {
                    title: 'Archived Videos',
                    middleware: [GateKeeperMiddleware],
                    ability: ({ gateKeeper }) => gateKeeper.userHasPermission('archive a video'),
                },
                props: true,
            },
        ],
    },
    {
        path: '/my-courses',
        name: 'corporate-my-courses',
        component: () => import('@/pages/corporate/MyCourses.vue'),
        meta: {
            title: 'My Courses',
            ...corporateStudentOnly,
        },
    },
    {
        path: '/course-library',
        name: 'corporate-course-library',
        component: () => import('@/pages/corporate/CourseLibrary.vue'),
        meta: {
            title: 'Course Library',
            ...corporateStudentOnly,
        },
    },
    {
        // Certificates is intentionally shared between corporate admins
        // and corporate students. Both personas need to reach `/certificates`:
        //   - Students arrive via the CorporateStudentSidebar item.
        //   - Admins arrive via the "Certificates" card on the Manage Users
        //     page (resources/js/pages/corporate/ManageUsers.vue).
        // We therefore omit the `corporateStudentOnly` / `corporateAdminOnly`
        // role gate here so neither role gets redirected to /restricted.
        // The client axis (`CorporateOnly`) is still attached by the mapper
        // below, so non-corporate clients remain locked out.
        path: '/certificates',
        name: 'corporate-certificates',
        component: () => import('@/pages/corporate/Certificates.vue'),
        meta: {
            title: 'Certificates',
        },
    },
    {
        // NOTE: must not start with `/admin` — the backend SPA catch-all in
        // routes/web.php excludes the `admin` prefix, so an `/admin/...` path
        // 404s on hard reload (client-side nav works, but a direct load/refresh
        // never receives the SPA shell). Keep this under `/certificates`.
        path: '/certificates/manage',
        name: 'corporate-admin-certificates',
        component: () => import('@/pages/corporate/AdminCertificates.vue'),
        meta: {
            title: 'Certificates',
            ...corporateAdminOnly,
        },
    },
    {
        path: '/manage-settings/certificates',
        name: 'corporate-certificate-settings',
        component: () => import('@/pages/corporate/CertificateSettings.vue'),
        meta: {
            title: 'Certificate Settings',
            ...corporateAdminOnly,
        },
    },
];

export default routes.map(route => ({
    ...route,
    beforeEnter: route.beforeEnter
        ? [CorporateOnly, ...(Array.isArray(route.beforeEnter) ? route.beforeEnter : [route.beforeEnter])]
        : CorporateOnly,
}));
